import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import {
	ANALYTICS_OPERATIONAL_SUBJECT,
	type AnalyticsOrigin,
	ANALYTICS_UI_SUBJECT_ID,
} from '../constants/snippetAnalytics.ts';
import { useSnippetAnalytics } from '../hooks/analytics/useSnippetAnalytics.ts';
import type { Snippet } from '../types/snippet.ts';
import { useSnippetsActions } from '../store/snippetsStore.ts';

export interface SnippetFavoriteButtonProps {
	analyticsOrigin: AnalyticsOrigin;
	snippet: Snippet;
	isHovered: boolean;
}

const styles = cssMap({
	container: {
		flexShrink: 0,
		opacity: 0,
		transition: 'opacity 0.15s ease-in-out',

		'&:focus-within': {
			opacity: 1,
		},
	},
	containerVisible: {
		opacity: 1,
	},
});

const messages = defineMessages({
	favoritedLabel: {
		id: 'snippets.snippetFavoriteButton.favoritedLabel',
		defaultMessage: 'Unfavorite Snippet',
		description: 'Label for the favorite button when a snippet is favorited',
	},
	unfavoritedLabel: {
		id: 'snippets.snippetFavoriteButton.unfavoritedLabel',
		defaultMessage: 'Favorite Snippet',
		description: 'Label for the favorite button when a snippet is not favorited',
	},
});

const SnippetFavoriteButtonInner = ({
	analyticsOrigin,
	snippet,
	isHovered,
	flags,
}: SnippetFavoriteButtonProps & WithFlagsProps) => {
	const intl = useIntl();
	const { fireButtonClickedAnalytic, fireFailedAnalytic } = useSnippetAnalytics(analyticsOrigin);
	const snippetsActions = useSnippetsActions();
	const shouldShowIcon = snippet.isFavorite || isHovered;
	const options = {
		intl,
		flags,
	};

	const handleFavoriteToggle = async (isFavoriteAfterToggle: boolean) => {
		const action = isFavoriteAfterToggle
			? snippetsActions.favoriteSnippet(snippet.snippetId, options)
			: snippetsActions.unfavoriteSnippet(snippet.snippetId, options);

		const success = await action;

		if (success) {
			fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.SNIPPET_FAVORITE_TOGGLED, {
				snippetId: snippet.snippetId,
				scope: snippet.scope,
				isFavoriteAfterToggle,
			});
		} else {
			fireFailedAnalytic(ANALYTICS_OPERATIONAL_SUBJECT.SNIPPET_FAVORITE_TOGGLED, {
				snippetId: snippet.snippetId,
				scope: snippet.scope,
				isFavoriteAfterToggle,
			});
		}
	};

	return (
		<Box xcss={cx(styles.container, shouldShowIcon && styles.containerVisible)}>
			{snippet.isFavorite ? (
				<IconButton
					label={intl.formatMessage(messages.favoritedLabel)}
					icon={(iconProps) => (
						<StarStarredIcon {...iconProps} color={token('color.icon.accent.orange')} label="" />
					)}
					appearance="subtle"
					onClick={() => handleFavoriteToggle(false)}
				/>
			) : (
				<IconButton
					label={intl.formatMessage(messages.unfavoritedLabel)}
					icon={StarUnstarredIcon}
					appearance="subtle"
					onClick={() => handleFavoriteToggle(true)}
				/>
			)}
		</Box>
	);
};

export const SnippetFavoriteButton = withFlags(SnippetFavoriteButtonInner);
